import { writeVarUint, writeVarUint8Array, createEncoder, length, toUint8Array } from 'lib0/encoding';
import { readVarUint8Array, createDecoder, readVarUint } from 'lib0/decoding';
import { readSyncMessage, messageYjsSyncStep2, writeSyncStep1, writeSyncStep2, writeUpdate } from 'y-protocols/sync';
import { encodeAwarenessUpdate, applyAwarenessUpdate, removeAwarenessStates, Awareness } from 'y-protocols/awareness';
import { readAuthMessage } from 'y-protocols/auth';
import { publish, subscribe, unsubscribe } from 'lib0/broadcastchannel';

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, _toPropertyKey(descriptor.key), descriptor);
  }
}
function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", {
    writable: false
  });
  return Constructor;
}
function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _toPrimitive(input, hint) {
  if (typeof input !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== undefined) {
    var res = prim.call(input, hint || "default");
    if (typeof res !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (hint === "string" ? String : Number)(input);
}
function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, "string");
  return typeof key === "symbol" ? key : String(key);
}

var _messageHandlers;
var MessageType;
(function (MessageType) {
  MessageType[MessageType["Sync"] = 0] = "Sync";
  MessageType[MessageType["Awareness"] = 1] = "Awareness";
  MessageType[MessageType["Auth"] = 2] = "Auth";
  MessageType[MessageType["QueryAwareness"] = 3] = "QueryAwareness";
})(MessageType || (MessageType = {}));
var permissionDeniedHandler = function permissionDeniedHandler(provider, reason) {
  console.warn("Permission denied to access " + provider.channelName + ".\n" + reason);
};
var messageHandlers = (_messageHandlers = {}, _messageHandlers[MessageType.Sync] = function (encoder, decoder, provider, emitSynced) {
  writeVarUint(encoder, MessageType.Sync);
  var syncMessageType = readSyncMessage(decoder, encoder, provider.doc, provider);
  if (emitSynced && syncMessageType === messageYjsSyncStep2 && !provider.synced) {
    provider.synced = true;
  }
}, _messageHandlers[MessageType.QueryAwareness] = function (encoder, _decoder, provider, _emitSynced, _messageType) {
  writeVarUint(encoder, MessageType.Awareness);
  writeVarUint8Array(encoder, encodeAwarenessUpdate(provider.awareness, Array.from(provider.awareness.getStates().keys())));
}, _messageHandlers[MessageType.Awareness] = function (_encoder, decoder, provider) {
  applyAwarenessUpdate(provider.awareness, readVarUint8Array(decoder), provider);
}, _messageHandlers[MessageType.Auth] = function (_encoder, decoder, provider) {
  readAuthMessage(decoder, provider.doc, function (_ydoc, reason) {
    return permissionDeniedHandler(provider, reason);
  });
}, _messageHandlers);
var WebsocketProvider = /*#__PURE__*/function () {
  function WebsocketProvider(doc, consumer, channel, params, _temp) {
    var _this = this;
    var _ref = _temp === void 0 ? {} : _temp,
      _ref$awareness = _ref.awareness,
      awareness = _ref$awareness === void 0 ? new Awareness(doc) : _ref$awareness,
      _ref$disableBc = _ref.disableBc,
      disableBc = _ref$disableBc === void 0 ? false : _ref$disableBc;
    this.bcSubscriber = function (data, origin) {
      if (origin !== _this) {
        var encoder = _this.process(new Uint8Array(data), false);
        if (length(encoder) > 1) {
          publish(_this.bcChannelName, toUint8Array(encoder), _this);
        }
      }
    };
    this.updateHandler = function (update, origin) {
      if (origin !== _this) {
        var encoder = createEncoder();
        writeVarUint(encoder, MessageType.Sync);
        writeUpdate(encoder, update);
        _this.send(toUint8Array(encoder));
      }
    };
    this.unloadHandler = function () {
      removeAwarenessStates(_this.awareness, [_this.doc.clientID], 'window unload');
    };
    this.awarenessUpdateHandler = function (_ref2, _origin) {
      var added = _ref2.added,
        updated = _ref2.updated,
        removed = _ref2.removed;
      var changedClients = added.concat(updated).concat(removed);
      var encoder = createEncoder();
      writeVarUint(encoder, MessageType.Awareness);
      writeVarUint8Array(encoder, encodeAwarenessUpdate(_this.awareness, changedClients));
      _this.send(toUint8Array(encoder));
    };
    this.consumer = consumer;
    this.channelName = channel;
    this.bcChannelName = channel + "_" + Object.entries(params).map(function (k, v) {
      return k + "-" + v;
    }).join('_');
    this.params = params;
    this.doc = doc;
    this.awareness = awareness;
    this.bcconnected = false;
    this.disableBc = disableBc;
    this._synced = false;
    this.doc.on('update', this.updateHandler);
    if (typeof window !== 'undefined') {
      window.addEventListener('unload', this.unloadHandler);
    } else if (typeof process !== 'undefined') {
      process.on('exit', this.unloadHandler);
    }
    awareness.on('update', this.awarenessUpdateHandler);
    this.connect();
  }
  var _proto = WebsocketProvider.prototype;
  _proto.destroy = function destroy() {
    this.disconnect();
    if (typeof window !== 'undefined') {
      window.removeEventListener('unload', this.unloadHandler);
    } else if (typeof process !== 'undefined') {
      process.off('exit', this.unloadHandler);
    }
    this.awareness.off('update', this.awarenessUpdateHandler);
    this.doc.off('update', this.updateHandler);
  };
  _proto.send = function send(buffer) {
    var _this$channel;
    var update = encodeBinaryToBase64(buffer);
    (_this$channel = this.channel) == null ? void 0 : _this$channel.send({
      update: update
    });
    if (this.bcconnected) {
      publish(this.bcChannelName, buffer, this);
    }
  };
  _proto.process = function process(buffer, emitSynced) {
    var decoder = createDecoder(buffer);
    var encoder = createEncoder();
    var messageType = readVarUint(decoder);
    var messageHandler = messageHandlers[messageType];
    if (messageHandler) {
      messageHandler(encoder, decoder, this, emitSynced, messageType);
    } else {
      console.error('Unable to compute message');
    }
    return encoder;
  };
  _proto.subscribe = function subscribe() {
    var provider = this;
    this.synced = false;
    this.channel = this.consumer.subscriptions.create(_extends({
      channel: this.channelName
    }, this.params), {
      received: function received(message) {
        var encodedUpdate = message.update;
        var update = decodeBase64ToBinary(encodedUpdate);
        var encoder = provider.process(update, true);
        if (length(encoder) > 1) {
          provider.send(toUint8Array(encoder));
        }
      },
      disconnected: function disconnected() {
        provider.synced = false;
        // update awareness (all users except local left)
        removeAwarenessStates(provider.awareness, Array.from(provider.awareness.getStates().keys()).filter(function (client) {
          return client !== provider.doc.clientID;
        }), provider);
      },
      connected: function connected() {
        // always send sync step 1 when connected
        var encoder = createEncoder();
        writeVarUint(encoder, MessageType.Sync);
        writeSyncStep1(encoder, provider.doc);
        provider.send(toUint8Array(encoder));
        // broadcast local awareness state
        if (provider.awareness.getLocalState() !== null) {
          var encoderAwarenessState = createEncoder();
          writeVarUint(encoderAwarenessState, MessageType.Awareness);
          writeVarUint8Array(encoderAwarenessState, encodeAwarenessUpdate(provider.awareness, [provider.doc.clientID]));
          provider.send(toUint8Array(encoderAwarenessState));
        }
      }
    });
  };
  _proto.connectBc = function connectBc() {
    if (this.disableBc) {
      return;
    }
    if (!this.bcconnected) {
      subscribe(this.bcChannelName, this.bcSubscriber);
      this.bcconnected = true;
    }
    // send sync step1 to bc
    // write sync step 1
    var encoderSync = createEncoder();
    writeVarUint(encoderSync, MessageType.Sync);
    writeSyncStep1(encoderSync, this.doc);
    publish(this.bcChannelName, toUint8Array(encoderSync), this);
    // broadcast local state
    var encoderState = createEncoder();
    writeVarUint(encoderState, MessageType.Sync);
    writeSyncStep2(encoderState, this.doc);
    publish(this.bcChannelName, toUint8Array(encoderState), this);
    // write queryAwareness
    var encoderAwarenessQuery = createEncoder();
    writeVarUint(encoderAwarenessQuery, MessageType.QueryAwareness);
    publish(this.bcChannelName, toUint8Array(encoderAwarenessQuery), this);
    // broadcast local awareness state
    var encoderAwarenessState = createEncoder();
    writeVarUint(encoderAwarenessState, MessageType.Awareness);
    writeVarUint8Array(encoderAwarenessState, encodeAwarenessUpdate(this.awareness, [this.doc.clientID]));
    publish(this.bcChannelName, toUint8Array(encoderAwarenessState), this);
  };
  _proto.disconnectBc = function disconnectBc() {
    // broadcast message with local awareness state set to null (indicating disconnect)
    var encoder = createEncoder();
    writeVarUint(encoder, MessageType.Awareness);
    writeVarUint8Array(encoder, encodeAwarenessUpdate(this.awareness, [this.doc.clientID], new Map()));
    this.send(toUint8Array(encoder));
    if (this.bcconnected) {
      unsubscribe(this.bcChannelName, this.bcSubscriber);
      this.bcconnected = false;
    }
  };
  _proto.disconnect = function disconnect() {
    var _this$channel2;
    this.disconnectBc();
    (_this$channel2 = this.channel) == null ? void 0 : _this$channel2.unsubscribe();
    if (this.channel != null) {
      this.channel = undefined;
    }
  };
  _proto.connect = function connect() {
    if (this.channel == null) {
      this.subscribe();
      this.connectBc();
    }
  };
  _createClass(WebsocketProvider, [{
    key: "synced",
    get: function get() {
      return this._synced;
    },
    set: function set(state) {
      if (this._synced !== state) {
        this._synced = state;
      }
    }
  }]);
  return WebsocketProvider;
}();
function encodeBinaryToBase64(bin) {
  var chars = Array.from(bin, function (ch) {
    return String.fromCharCode(ch);
  }).join('');
  return btoa(chars);
}
function decodeBase64ToBinary(update) {
  return Uint8Array.from(atob(update), function (c) {
    return c.charCodeAt(0);
  });
}

export { WebsocketProvider };
//# sourceMappingURL=actioncable.esm.js.map
